import { useEffect, useState, useRef } from "react";
import { motion, useScroll, useTransform, useSpring } from "framer-motion";
import { useInView } from "react-intersection-observer";
import IconCloud from "../components/IconCloud";
import { Layout } from "antd";
import axios from "axios";
import ImageLoader from "../components/ImageLoader";
import Products from "../components/Products";
import Loader from "../components/Loader";
import { fetchMediaMap } from "../utils/media";
import RotatingText from "../components/RotatingText";
import ScrollVelocity from "../components/ScrollVelocity";
import {
  Code,
  Rocket,
  Shield,
  Zap,
  ArrowRight,
  CheckCircle,
  TrendingUp,
  Users,
  Globe,
} from "lucide-react";
import AnimatedStats from "../components/AnimatedStats";
import GrowthSection from "../components/GrowthSection";
import HeroSection from "../components/HeroSection";
import CodingBackground from "../components/CodingBackground";

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 120, rotateX: -25, scale: 0.9, filter: 'blur(15px)', z: -100 },
  visible: {
    opacity: 1,
    y: 0,
    rotateX: 0,
    scale: 1,
    filter: 'blur(0px)',
    z: 0,
    transition: { type: 'spring', stiffness: 50, damping: 20, mass: 1 },
  },
};

const SectionWrapper = ({ children, className = "" }) => {
  return (
    <motion.div
      initial="hidden"
      whileInView="visible"
      viewport={{ once: false, amount: 0.1 }}
      variants={fadeInUp}
      className={className}
      style={{ perspective: "1000px", transformStyle: "preserve-3d" }}
    >
      {children}
    </motion.div>
  );
};

const Home = () => {
  const [pagedata, setPagedata] = useState({});
  const [loading, setLoading] = useState(true);
  const [sliders, setSliders] = useState([]);
  const [about, setAbout] = useState([]);
  const [services, setServices] = useState([]);
  const [skills, setSkills] = useState({});
  const [clients, setClients] = useState({});
  const [team, setTeam] = useState({});

  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll();
  const opacity = useTransform(scrollYProgress, [0, 0.3], [1, 0.8]);
  const scale = useTransform(scrollYProgress, [0, 0.3], [1, 0.97]);

  const mouseX = useSpring(0, { stiffness: 100, damping: 30 });
  const mouseY = useSpring(0, { stiffness: 100, damping: 30 });

  useEffect(() => {
    fetchData();

    const handleMouseMove = (e) => {
      const rect = heroRef.current?.getBoundingClientRect();
      if (rect) {
        const x = (e.clientX - rect.left) / rect.width - 0.5;
        const y = (e.clientY - rect.top) / rect.height - 0.5;
        mouseX.set(x * 20);
        mouseY.set(y * 20);
      }
    };

    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, [mouseX, mouseY]);

  const fetchData = async () => {
    try {
      const response = await axios.get("https://emr.metadei.in/wp-json/wp/v2/pages/15");
      const acf = response.data?.acf || {};

      setPagedata(acf);
      setSliders(acf.sliders_home || []);
      setSkills(acf.skills_home || {});
      setTeam(acf.team_home || {});

      const rawAbout = acf.about_us_box_home?.items_about_us_box_home || [];
      const rawServices = acf.services_box_home?.items_services_box_home || [];
      const rawClients = acf.clients_home?.logos_skills_home || [];

      // Collect all numeric image IDs to resolve in one batch
      const aboutIds = rawAbout.map((item) => item.image_about_us_home).filter(Boolean);
      const serviceIds = rawServices.map((item) => item.image_services_home).filter(Boolean);
      const clientIds = rawClients.map((item) => item.logo_logos_skills_home).filter(Boolean);

      const allIds = [...aboutIds, ...serviceIds, ...clientIds];
      const mediaMap = await fetchMediaMap(allIds);

      const resolvedAbout = rawAbout.map((item) => ({
        ...item,
        image_about_us_home: mediaMap[item.image_about_us_home] || (typeof item.image_about_us_home === 'string' ? item.image_about_us_home : ''),
      }));

      const resolvedServices = rawServices.map((item) => ({
        ...item,
        image_services_home: mediaMap[item.image_services_home] || (typeof item.image_services_home === 'string' ? item.image_services_home : ''),
      }));

      const resolvedClients = {
        ...(acf.clients_home || {}),
        logos_skills_home: rawClients.map((item) => ({
          ...item,
          logo_logos_skills_home: mediaMap[item.logo_logos_skills_home] || item.logo_logos_skills_home,
        })),
      };

      setAbout(resolvedAbout);
      setServices(resolvedServices);
      setClients(resolvedClients);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching Home page data:", err);
      setLoading(false);
    }
  };

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <div className="bg-background text-foreground min-h-screen overflow-hidden transition-colors duration-300">
          <Layout className="bg-transparent">
            <div>
              <HeroSection sliders={sliders} />

              {/* Features Section - 3D Stepped Design */}
              <SectionWrapper className="py-16 md:py-24 relative overflow-hidden group">
                <CodingBackground />
                <div className="absolute top-0 right-0 w-[300px] md:w-[500px] h-[300px] md:h-[500px] bg-primary/10 blur-[100px] md:blur-[150px] rounded-full pointer-events-none" />
                <div className="max-w-7xl mx-auto px-4 relative z-10">
                  <div className="text-center mb-16">
                    <span className="text-primary font-bold text-xs uppercase tracking-widest font-secondary block mb-2">
                      Why Choose Us
                    </span>
                    <h2 className="text-4xl md:text-5xl font-black font-heading mb-4">
                      What Makes Us Different
                    </h2>
                    <div className="w-16 h-1 bg-gradient-to-r from-primary to-accent mx-auto rounded-full" />
                    <p className="text-muted-foreground mt-6 max-w-2xl mx-auto font-body text-lg">
                      We combine technical expertise with deep business understanding
                      to deliver exceptional, scalable results.
                    </p>
                  </div>

                  {/* Grid with perspective for 3D effect */}
                  <div
                    className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
                    style={{ perspective: "1000px" }}
                  >
                    {[
                      {
                        icon: Rocket,
                        title: "Innovation First",
                        desc: "Cutting-edge solutions using the latest technologies.",
                        delay: 0,
                        large: false,
                      },
                      {
                        icon: Shield,
                        title: "Security Focused",
                        desc: "Enterprise-grade security embedded by design.",
                        delay: 0.1,
                        large: true,
                      },
                      {
                        icon: Zap,
                        title: "Fast Delivery",
                        desc: "Agile methodologies for rapid, reliable deployments.",
                        delay: 0.2,
                        large: true,
                      },
                      {
                        icon: TrendingUp,
                        title: "Scalable Solutions",
                        desc: "Architectures built to scale effortlessly with you.",
                        delay: 0.3,
                        large: false,
                      },
                    ].map((feature, idx) => (
                      <motion.div
                        key={idx}
                        initial={{ opacity: 0, y: 100, rotateX: -25, scale: 0.8, z: -150 }}
                        whileInView={{ opacity: 1, y: 0, rotateX: 0, scale: 1, z: 0 }}
                        transition={{ duration: 0.8, delay: feature.delay, type: "spring", stiffness: 100, damping: 20 }}
                        viewport={{ once: false, margin: "-50px" }}
                        whileHover={{
                          scale: 1.02,
                          y: -10,
                          transition: {
                            duration: 0.3,
                            type: "spring",
                            stiffness: 300,
                          },
                        }}
                        className={`group relative ${feature.large ? "lg:-translate-y-4" : "lg:translate-y-4"}`}
                        style={{ transformStyle: "preserve-3d" }}
                      >
                        <div className="bg-white rounded-3xl p-8 shadow-sm hover:shadow-[0_8px_30px_rgb(0,0,0,0.12)] transition-all duration-500 border border-gray-100 h-full flex flex-col relative overflow-hidden hover-glow dark:bg-gray-800 dark:border-gray-700">
                          {/* Gradient Background Hover */}
                          <div className="absolute inset-0 bg-gradient-to-br from-primary/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                          {/* Icon Container */}
                          <div className="relative mb-6 z-10 w-16 h-16 rounded-2xl flex items-center justify-center bg-primary/10 border border-primary/20 transition-all duration-300 group-hover:scale-110 group-hover:bg-primary">
                            <feature.icon className="w-8 h-8 text-primary group-hover:text-white drop-shadow-sm transition-colors" />
                          </div>

                          {/* Content */}
                          <h3 className="text-xl font-bold font-heading mb-3 text-gray-900 group-hover:text-primary transition-colors duration-300 relative z-10 dark:text-white">
                            {feature.title}
                          </h3>
                          <p className="text-gray-500 text-sm leading-relaxed flex-grow font-body relative z-10 dark:text-gray-400">
                            {feature.desc}
                          </p>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </SectionWrapper>

              {/* Product Portfolio */}
              <SectionWrapper className="py-16 md:py-24 border-y border-border relative overflow-hidden">
                <CodingBackground />
                <div className="absolute inset-0 bg-muted/30 pointer-events-none" />
                <div className="max-w-7xl mx-auto px-4 relative z-10">
                  <div className="text-center mb-16">
                    <span className="text-primary font-bold text-xs uppercase tracking-widest font-secondary block mb-2">
                      Our Work
                    </span>
                    <h2 className="text-4xl md:text-5xl font-black font-heading mb-4">
                      Product Portfolio
                    </h2>
                    <div className="w-16 h-1 bg-gradient-to-r from-primary to-accent mx-auto rounded-full" />
                    <p className="text-muted-foreground mt-6 max-w-2xl mx-auto font-body text-lg">
                      Discover our elite solutions that have helped forward-thinking
                      businesses dominate their markets.
                    </p>
                  </div>
                  <Products />
                </div>
              </SectionWrapper>

              <GrowthSection />

              {/* <AnimatedStats /> */}

              {/* Mission Vision Section */}
              <div className="py-16 md:py-24 relative overflow-hidden bg-background">
                <CodingBackground />
                <div className="absolute left-0 top-1/2 -translate-y-1/2 w-full md:w-1/3 h-[300px] md:h-[500px] bg-secondary/10 blur-[100px] md:blur-[150px] rounded-full pointer-events-none" />
                <div className="max-w-7xl mx-auto px-4 relative z-10">
                  <div className="text-center mb-16">
                    <span className="text-primary font-bold text-xs uppercase tracking-widest font-secondary block mb-2">
                      Our Mission & Vision
                    </span>
                    <h2 className="text-4xl md:text-5xl font-black font-heading mb-4">
                      What Drives Us
                    </h2>
                    <div className="w-16 h-1 bg-gradient-to-r from-primary to-accent mx-auto rounded-full" />
                    <p className="text-muted-foreground mt-6 max-w-2xl mx-auto font-body text-lg">
                      Committed to engineering excellence, guided by core values that shape the future.
                    </p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {about.map((item, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, y: 100, rotateY: 20, rotateX: 20, scale: 0.8, z: -200 }}
                        whileInView={{ opacity: 1, y: 0, rotateY: 0, rotateX: 0, scale: 1, z: 0 }}
                        transition={{ duration: 0.8, delay: index * 0.15, type: "spring", stiffness: 80, damping: 15 }}
                        viewport={{ once: false, margin: "-50px" }}
                        style={{ transformStyle: "preserve-3d" }}
                        className="bg-card/50 backdrop-blur-md rounded-2xl overflow-hidden shadow-sm hover:shadow-xl border border-border transition-all duration-500 group hover-glow"
                      >
                        <div className="h-56 overflow-hidden relative">
                          <img
                            src={item.image_about_us_home}
                            alt={item.heading_about_us_home}
                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = 'https://emr.metadei.in/wp-content/uploads/2024/12/vision.png';
                            }}
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-background to-transparent opacity-80" />
                        </div>
                        <div className="p-8 relative -mt-8 bg-card/90 backdrop-blur-sm rounded-t-3xl border-t border-border">
                          <h3 className="text-2xl font-bold font-heading mb-3">
                            {item.heading_about_us_home}
                          </h3>
                          <div className="w-12 h-1 bg-gradient-to-r from-primary to-accent rounded-full mb-4" />
                          <p className="text-muted-foreground leading-relaxed font-body text-sm">
                            {item.writeup_about_us_home}
                          </p>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Scrolling Text Section */}
              <div className="py-24 border-y border-border overflow-hidden relative">
                <CodingBackground />
                <div className="absolute inset-0 bg-primary/5 [mask-image:linear-gradient(to_bottom,transparent,black,transparent)] pointer-events-none" />
                {/* Line 1 - Multiple words */}
                <ScrollVelocity
                  texts={[
                    "Innovation  Excellence  Technology  Creativity  Solutions  Future  Growth  Success  Impact  Quality",
                  ]}
                  velocity={40}
                  className="font-secondary text-3xl md:text-5xl lg:text-7xl font-black bg-gradient-to-r from-foreground to-muted-foreground bg-clip-text text-transparent uppercase tracking-wider"
                  numCopies={4}
                  damping={30}
                  stiffness={300}
                />

                {/* Line 2 - Multiple words */}
                <div className="mt-8">
                  <ScrollVelocity
                    texts={[
                      "Digital Transformation  Software Development  Cloud Solutions  AI & ML  Web Applications  Mobile Apps",
                    ]}
                    velocity={-35}
                    className="font-secondary text-3xl md:text-5xl lg:text-7xl font-black bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent uppercase tracking-wider"
                    numCopies={4}
                    damping={30}
                    stiffness={300}
                  />
                </div>
              </div>

              {/* Services Section */}
              <div className="py-16 md:py-24 relative overflow-hidden">
                <CodingBackground />
                <div className="absolute bottom-0 right-0 w-[400px] md:w-[600px] h-[400px] md:h-[600px] bg-secondary/10 blur-[100px] md:blur-[150px] rounded-full pointer-events-none" />
                <div className="max-w-7xl mx-auto px-4 relative z-10">
                  <div className="text-center mb-16">
                    <span className="text-primary font-bold text-xs uppercase tracking-widest font-secondary block mb-2">
                      What We Offer
                    </span>
                    <h2 className="text-4xl md:text-5xl font-black font-heading mb-4">
                      Our Services
                    </h2>
                    <div className="w-16 h-1 bg-gradient-to-r from-primary to-accent mx-auto rounded-full" />
                    <p className="text-muted-foreground mt-6 max-w-2xl mx-auto font-body text-lg">
                      Comprehensive, state-of-the-art solutions tailored to dominate your market.
                    </p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {services.map((service, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, y: 80, rotateX: -30, scale: 0.9, filter: 'blur(10px)', z: -100 }}
                        whileInView={{ opacity: 1, y: 0, rotateX: 0, scale: 1, filter: 'blur(0px)', z: 0 }}
                        transition={{ duration: 0.7, delay: index * 0.1, type: "spring", stiffness: 90, damping: 18 }}
                        viewport={{ once: false, margin: "-50px" }}
                        style={{ transformStyle: "preserve-3d" }}
                        className="bg-card/50 backdrop-blur-sm border border-border rounded-2xl p-8 shadow-sm hover:shadow-xl transition-all duration-300 text-center group hover-glow"
                      >
                        <div className="flex justify-center mb-6">
                          <div className="w-20 h-20 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center group-hover:scale-110 group-hover:-rotate-3 transition-transform duration-300">
                            <img
                              src={service.image_services_home}
                              alt=""
                              className="w-10 h-10 object-contain drop-shadow-md"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = 'https://emr.metadei.in/wp-content/uploads/2024/12/enable_service.png';
                              }}
                            />
                          </div>
                        </div>
                        <h3 className="text-xl font-bold font-heading mb-3 transition-colors group-hover:text-primary">
                          {service.heading_services_home}
                        </h3>
                        <p className="text-muted-foreground text-sm leading-relaxed font-body">
                          {service.writeup_services_home}
                        </p>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Skills Section */}
              <div className="py-24 bg-muted/30 border-y border-border relative overflow-hidden">
                <CodingBackground />
                <div className="max-w-7xl mx-auto px-4 relative z-10">
                  <div className="text-center mb-16">
                    <span className="text-primary font-bold text-xs uppercase tracking-widest font-secondary block mb-2">
                      Our Expertise
                    </span>
                    <h2 className="text-4xl md:text-5xl font-black font-heading mb-4">
                      {skills.heading_skills_home || "Technologies We Master"}
                    </h2>
                    <div className="w-16 h-1 bg-gradient-to-r from-primary to-accent mx-auto rounded-full" />
                    <p className="text-muted-foreground mt-6 max-w-2xl mx-auto font-body text-lg">
                      {skills.writeup_skills_home ||
                        "Leveraging the latest elite technologies to deliver unparalleled results."}
                    </p>
                  </div>
                  <IconCloud />
                </div>
              </div>

              {/* Clients Section */}
              <div className="py-16 md:py-24 relative overflow-hidden">
                <CodingBackground />
                <div className="max-w-7xl mx-auto px-4 relative z-10">
                  <div className="text-center mb-16">
                    <span className="text-primary font-bold text-xs uppercase tracking-widest font-secondary block mb-2">
                      Trusted Partners
                    </span>
                    <h2 className="text-4xl md:text-5xl font-black font-heading mb-4">
                      {clients.heading_skills_home || "Our Clients"}
                    </h2>
                    <div className="w-16 h-1 bg-gradient-to-r from-primary to-accent mx-auto rounded-full" />
                  </div>
                </div>

                {/* Sliding Marquee */}
                <div className="relative w-full overflow-hidden py-10">
                  <motion.div
                    className="flex whitespace-nowrap gap-6"
                    animate={{ x: ["0%", "-50%"] }}
                    transition={{
                      x: {
                        repeat: Infinity,
                        repeatType: "loop",
                        duration: 30,
                        ease: "linear",
                      },
                    }}
                    style={{ width: "fit-content" }}
                  >
                    {/* Duplicate array for seamless loop */}
                    {[...(clients.logos_skills_home || []), ...(clients.logos_skills_home || [])].map((client, index) => (
                      <div
                        key={index}
                        className="bg-white border border-border rounded-xl p-8 shadow-sm hover:border-primary/50 hover:shadow-md transition-all duration-300 flex items-center justify-center grayscale hover:grayscale-0 min-w-[200px]"
                      >
                        <div className="transition-transform duration-300 hover:scale-105">
                          <ImageLoader
                            url={client.logo_logos_skills_home}
                            ht={60}
                          />
                        </div>
                      </div>
                    ))}
                  </motion.div>
                </div>
              </div>
            </div>
          </Layout>
        </div>
      )}
    </>
  );
};

export default Home;
